import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ImportErrorPopupComponent } from '@shared/components/import-error-popup/import-error-popup.component';
import { UploadStatus, ValidatorService } from '@shared/models/fileupload';
import { Subject } from 'rxjs';
import { ImportResponse, ImportStatus, ITransferService } from '../export-import-services/interfaces';

@Injectable({
	providedIn: 'root',
})
export class ImportValidatorService implements ValidatorService {
	private statusSubject: Subject<UploadStatus> = new Subject<UploadStatus>();
	public statusSubject$ = this.statusSubject.asObservable();

	constructor(private dialog: MatDialog) {}

	validate(files: File[], fields: Record<string, FormControl>, context: ITransferService<any>) {
		const overwrite = (fields['overwrite']?.value as boolean) || false;
		let savedResult: ImportResponse | undefined;
		context.importData(files, overwrite).subscribe({
			next: (result) => {
				savedResult = result;
			},
			complete: () => {
				const reported = savedResult?.errors ?? [];
				// `ImportStatus.Skipped` shares the `errors` array with real failures, but a skip is
				// a deliberate no-op: re-running an unchanged file without `overwrite` ticked is a
				// correct outcome, not something to "correct and try again". Counting failures
				// separately is what stops an all-skipped run raising the error toast (R44).
				const failures = reported.filter((error) => error.status !== ImportStatus.Skipped);

				if (failures.length > 0) {
					this.statusSubject.next({ status: 'error', data: null, close: true });
				} else {
					this.statusSubject.next({ status: 'success', data: null });
				}

				// Skipped rows are still worth showing — the popup names them in their own section
				// with their own count, so the user learns nothing was overwritten.
				if (reported.length > 0) {
					this.dialog.open(ImportErrorPopupComponent, {
						width: '600px',
						data: savedResult,
					});
				}
			},
			error: (error: Error) => {
				this.statusSubject.next({ status: 'error', data: error.message });
			},
		});
	}
}
